import { Button, Checkbox, Input, Popover, PopoverContent, PopoverTrigger, SegmentedButtonGroup, Text } from '@olytic/foundation';
import { Link } from 'react-router-dom';

import type { BusinessCase } from './businessCase';
import { ProposalDocument } from './ProposalDocument';
import { applyLayout, type InvestmentSettings, LAYOUTS, type LayoutName, move, type ProposalLayout, titleOf } from './proposal';
import type { QuoteBuilder } from './quotesApi';
import type { Terms, Timeline } from '@quote-math/terms';
import type { ValueModel } from '@quote-math/valueModel';

const TOGGLES: { key: keyof InvestmentSettings; label: string; rule?: boolean }[] = [
  { key: 'groups', label: 'Feature groupings' },
  { key: 'lines', label: 'Show line items' },
  { key: 'subtotals', label: 'Subtotals' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'listPrice', label: 'List price', rule: true },
  { key: 'price', label: 'Price' },
  { key: 'discount', label: 'Line discount ($)' },
  { key: 'totalDiscount', label: 'Total discount' },
  { key: 'withPayment', label: 'Payment terms in this section', rule: true },
  { key: 'scheduleOnly', label: 'Invoice schedule only' },
];

const today = () => new Date().toISOString().slice(0, 10);
const popHead = 'border-b border-[var(--border)] px-3.5 py-2.5 text-xs font-emphasis uppercase tracking-[0.12em] text-[var(--fg-muted)]';

/**
 * Proposal: the document itself, as the customer will see it, with the
 * investment table's settings and the sections (order, on or off, appendix)
 * above it. Its words (business case, payment, timeline, signatures, custom
 * sections) are on the Terms tab.
 */
export function ProposalTab({
  data,
  layout,
  memo,
  terms,
  timeline,
  valueModel,
  onSave,
}: {
  data: QuoteBuilder;
  layout: ProposalLayout;
  memo: BusinessCase;
  terms: Terms;
  timeline: Timeline;
  valueModel: ValueModel;
  onSave: (l: ProposalLayout) => void;
}): JSX.Element {
  const setInvestment = (patch: Partial<InvestmentSettings>) => onSave({ ...layout, investment: { ...layout.investment, ...patch } });
  const setSections = (sections: ProposalLayout['sections']) => onSave({ ...layout, sections });
  const patchSection = (key: string, patch: { enabled?: boolean; appendix?: boolean; title?: string }) =>
    setSections(layout.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Text tone="muted">The proposal draws on every tab. Its words are on the Terms tab.</Text>
        <div className="flex flex-wrap gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="neutral" size="sm">
                Investment table
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0">
              <div className={popHead}>Investment table</div>
              <div className="flex flex-col py-1.5">
                {TOGGLES.map((t) => (
                  <label
                    key={t.key}
                    className={`flex cursor-pointer items-center justify-between gap-3 px-3.5 py-1.5 text-[13px] hover:bg-[var(--surface-sunken)] ${t.rule ? 'mt-1.5 border-t border-[var(--border)] pt-2.5' : ''}`}
                  >
                    <span>{t.label}</span>
                    <Checkbox checked={Boolean(layout.investment[t.key])} onCheckedChange={(v) => setInvestment({ [t.key]: v === true })} />
                  </label>
                ))}
                <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-[var(--border)] px-3.5 pb-1 pt-2.5 text-[13px]">
                  <span>Recurring prices</span>
                  <SegmentedButtonGroup
                    label="Recurring prices"
                    size="sm"
                    options={[
                      { value: 'monthly', label: 'Monthly' },
                      { value: 'annual', label: 'Annual' },
                    ]}
                    value={layout.investment.annual ? 'annual' : 'monthly'}
                    onChange={(v) => setInvestment({ annual: v === 'annual' })}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="neutral" size="sm">
                Sections
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[500px] p-0">
              <div className={popHead}>Sections</div>
              <div className="flex items-center gap-2 border-b border-[var(--border)] px-3.5 py-2.5 text-[13px]">
                <span className="mr-auto">Layout</span>
                {(Object.keys(LAYOUTS) as LayoutName[]).map((name) => (
                  <Button key={name} variant="neutral" size="sm" onClick={() => setSections(applyLayout(layout.sections, name))}>
                    {LAYOUTS[name].label}
                  </Button>
                ))}
              </div>
              <ol className="oly-reset flex max-h-[560px] flex-col overflow-y-auto py-1">
                {layout.sections.map((s, i) => {
                  const name = titleOf(s, layout);
                  const pinned = s.key === 'contents' || s.key === 'signatures';
                  return (
                    <li key={s.key} className="flex items-center gap-1 px-3.5 py-1 text-[13px]">
                      {s.key.startsWith('custom:') || pinned ? (
                        <span className={`mr-auto truncate ${s.enabled ? '' : 'text-[var(--fg-muted)]'}`}>{name}</span>
                      ) : (
                        <Input
                          key={`${s.key}:${name}`}
                          fit="compact"
                          aria-label={`Title of ${name}`}
                          className="mr-auto min-w-0 flex-1"
                          defaultValue={name}
                          onBlur={(e) => {
                            const next = e.target.value.trim();
                            if (next !== name) patchSection(s.key, { title: next });
                          }}
                        />
                      )}
                      <Button variant="subtle" size="sm" aria-label={`Move ${name} up`} disabled={i === 0} onClick={() => setSections(move(layout.sections, i, -1))}>
                        ↑
                      </Button>
                      <Button
                        variant="subtle"
                        size="sm"
                        aria-label={`Move ${name} down`}
                        disabled={i === layout.sections.length - 1}
                        onClick={() => setSections(move(layout.sections, i, 1))}
                      >
                        ↓
                      </Button>
                      {pinned ? (
                        <span className="w-[84px]" />
                      ) : (
                        <Button
                          variant={s.appendix ? 'brand' : 'subtle'}
                          size="sm"
                          className="w-[84px]"
                          aria-pressed={Boolean(s.appendix)}
                          aria-label={`Put ${name} in the appendix`}
                          onClick={() => patchSection(s.key, { appendix: !s.appendix })}
                        >
                          Appendix
                        </Button>
                      )}
                      <Checkbox aria-label={`Show ${name}`} checked={s.enabled} onCheckedChange={(v) => patchSection(s.key, { enabled: v === true })} />
                    </li>
                  );
                })}
              </ol>
            </PopoverContent>
          </Popover>
          <Button asChild variant="brand" size="sm">
            <Link to={`/quotes/${data.quote.id}/proposal?print=1`} target="_blank" rel="noreferrer">
              Print or save as PDF
            </Link>
          </Button>
        </div>
      </div>
      <ProposalDocument data={data} layout={layout} memo={memo} terms={terms} timeline={timeline} valueModel={valueModel} prepared={today()} />
    </div>
  );
}
